import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Skeleton, useTheme, useMediaQuery } from "@mui/material";
import { fetchData, HTTP_STATUS, intl, DmsAltBox } from "dmsbasereact";
import { useDmsAnalyticsContext } from "./DmsAnalyticsContext";

const FlowValue = ({ label, value, color, isLoading }) => {
    const theme = useTheme();
    const colorValue = color.includes(".")
        ? color.split(".").reduce((obj, key) => obj?.[key], theme.palette)
        : theme.palette[color]?.main || theme.palette.primary.main;

    return (
        <Box sx={{ textAlign: "center", minWidth: 80, px: 1 }}>
            <Typography
                variant="caption"
                sx={{
                    color: "text.secondary",
                    display: "block",
                    fontWeight: 500,
                    fontSize: "0.7rem",
                    whiteSpace: "nowrap",
                }}
            >
                {label}
            </Typography>
            {isLoading ? (
                <Skeleton variant="text" width={50} sx={{ mx: "auto", height: 32 }} />
            ) : (
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        color: colorValue,
                        fontSize: "1.1rem",
                    }}
                >
                    {value !== null && value !== undefined ? value.toLocaleString("pt-BR") : "--"}
                </Typography>
            )}
        </Box>
    );
};

FlowValue.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.number,
    color: PropTypes.string,
    isLoading: PropTypes.bool,
};

const Operator = ({ symbol }) => (
    <Typography
        variant="h5"
        sx={{
            fontWeight: "bold",
            color: "text.secondary",
            mx: 0.5,
            alignSelf: "center",
        }}
    >
        {symbol}
    </Typography>
);

Operator.propTypes = {
    symbol: PropTypes.string.isRequired,
};

const EquationRow = ({ items, data, isLoading, isMobile }) => {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexWrap: isMobile ? "wrap" : "nowrap",
                gap: isMobile ? 1 : 0,
                py: 1,
            }}
        >
            {items.map((item, index) => {
                if (item.operator) {
                    return <Operator key={`op-${index}`} symbol={item.operator} />;
                }
                return (
                    <FlowValue
                        key={item.key || index}
                        label={item.label}
                        value={data?.[item.dataKey] ?? null}
                        color={item.color || "primary.main"}
                        isLoading={isLoading}
                    />
                );
            })}
        </Box>
    );
};

EquationRow.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.shape({
                operator: PropTypes.string,
            }),
            PropTypes.shape({
                key: PropTypes.string,
                label: PropTypes.string.isRequired,
                dataKey: PropTypes.string.isRequired,
                color: PropTypes.string,
            }),
        ])
    ).isRequired,
    data: PropTypes.object,
    isLoading: PropTypes.bool,
    isMobile: PropTypes.bool,
};

export const DmsAnalyticsFlow = ({
    url,
    title,
    equations,
    data: externalData,
    children,
    sx,
    ...props
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { queryParams, refreshKey } = useDmsAnalyticsContext();

    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);

    const loadData = useCallback(async () => {
        if (!url) return;

        setLoading(true);
        try {
            const separator = url.includes("?") ? "&" : "?";
            const fullUrl = queryParams ? `${url}${separator}${queryParams}` : url;
            const response = await fetchData(fullUrl, "GET", true);

            if (response.status === HTTP_STATUS.OK_200) {
                setData(response.data?.data || {});
            }
        } catch (err) {
            console.error(`Error loading flow data from ${url}:`, err);
            setData({});
        } finally {
            setLoading(false);
        }
    }, [url, queryParams]);

    useEffect(() => {
        if (url) {
            loadData();
        }
    }, [url, loadData, refreshKey]);

    const displayData = externalData || data;
    const isLoading = url ? loading : false;

    return (
        <DmsAltBox.Card sx={sx} {...props}>
            <DmsAltBox.Header>
                <Typography variant="h6">{title}</Typography>
                {children && <DmsAltBox.HeaderRight>{children}</DmsAltBox.HeaderRight>}
            </DmsAltBox.Header>
            <DmsAltBox.Body>
                {equations && equations.length > 0 ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {equations.map((equation, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    borderBottom: idx < equations.length - 1 ? "1px solid" : "none",
                                    borderColor: "divider",
                                    pb: idx < equations.length - 1 ? 2 : 0,
                                }}
                            >
                                <EquationRow
                                    items={equation}
                                    data={displayData}
                                    isLoading={isLoading}
                                    isMobile={isMobile}
                                />
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 100,
                            color: "text.secondary",
                        }}
                    >
                        <Typography variant="body2">
                            {intl.get("DmsAnalytics.NoEquations").d("Nenhuma equação configurada")}
                        </Typography>
                    </Box>
                )}
            </DmsAltBox.Body>
        </DmsAltBox.Card>
    );
};

DmsAnalyticsFlow.propTypes = {
    url: PropTypes.string,
    title: PropTypes.string.isRequired,
    equations: PropTypes.arrayOf(
        PropTypes.arrayOf(
            PropTypes.oneOfType([
                PropTypes.shape({
                    operator: PropTypes.string,
                }),
                PropTypes.shape({
                    key: PropTypes.string,
                    label: PropTypes.string.isRequired,
                    dataKey: PropTypes.string.isRequired,
                    color: PropTypes.string,
                }),
            ])
        )
    ),
    data: PropTypes.object,
    children: PropTypes.node,
    sx: PropTypes.object,
};
